/**
 * Interface to brooklyn media assets API
 * @module services/media-assets
 */
import { stringify as qsStringifyQuery } from 'qs'
import get from 'lodash/get'
import parseInt from 'lodash/parseInt'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import { Map, fromJS, List, Set } from 'immutable'
import strictUriEncode from 'strict-uri-encode'
import request from '../request'
import { createAuthorization } from '../auth'
import { getLocationPathname, getLocationQuery } from '../location'
import matchPath from '../../routes/match-path'

/**
 * Media assets store key
 */
export const MEDIA_ASSETS_STORE_KEY = 'mediaAssets'

/**
 * Media assets store key for ingest urls
 */
export const MEDIA_ASSETS_INGEST_URLS_STORE_KEY = 'mediaAssetsIngestUrls'

/**
 * Media assets store key for ingest jobs
 */
export const MEDIA_ASSETS_INGEST_JOBS_STORE_KEY = 'mediaAssetsIngestJobs'

/**
 * Given the location state get the media id
 * @param {Map} location The location state
 * @returns {String|undefined} The media id if found in the location
 */
export function getMediaAssetIdFromLocation (location = Map()) {
  if (!Map.isMap(location)) {
    throw new Error('The location argument is required to be a Map')
  }
  const match = matchPath({
    pathname: getLocationPathname(location),
    path: '/media-assets/:id',
  })
  const id = get(match, 'params.id')
  return /[0-9]+$/.test(id) ? parseInt(id) : id
}

/**
 * Return true when on a media detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is a media detail page
 */
export function isMediaAssetCreatePage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const id = getMediaAssetIdFromLocation(location)
  return id === 'create'
}

/**
 * Given state create the media create options
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 */
export function createMediaAssetCreateViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    create: true,
    mediaId: query.get('mediaId'),
  })
}

/**
 * Create a media asset
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Map} options.data The media data to create
 */
export async function upsertMediaAsset (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The asset options is required to be a Map')
  }
  const res = await request
    .post(`${server}/v1/media-assets`)
    .send({
      mediaId: data.get('mediaId'),
      mediaReferenceType: data.get('mediaReferenceType'),
      originalFileName: data.get('originalFileName'),
      language: data.get('language'),
      provider: data.get('provider'),
      // If provider is brighcove, then providerKey = brightcove id
      providerKey: data.get('providerKey'),
      fileBase64: data.get('fileBase64'),
      mimeType: data.get('mimeType'),
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Delete a media asset from Brooklyn media-assets service
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Map} options.data The media data to create
 */
export async function deleteMediaAsset (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  if (!data.get('id')) {
    throw new Error('The data option is required to have a id property')
  }
  const res = await request
    .delete(`${server}/v1/media-assets/${strictUriEncode(data.get('id'))}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a media asset based on its id from brooklyn
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Map} options.data The media data to create
 */
export async function getMediaAssetById (options = {}) {
  const { auth, server, id } = options
  if (!id) {
    throw new Error('The id option is required')
  }
  const res = await request
    .get(`${server}/v1/media-assets/${strictUriEncode(id)}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a collection of existing media assets based on mediaId
 */
export async function getMediaAssetsByMediaId ({ auth, server, mediaId }) {
  if (!mediaId || mediaId.length === 0) {
    throw new Error('Please provide a mediaId')
  }
  try {
    const res = await request
      .get(`${server}/v1/media-assets/list`)
      .query({ mediaId })
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    return fromJS(get(res, 'body.mediaAssets', []))
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`No media assets found for ${mediaId}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Get a list of media assets from brooklyn
 */
export async function getMediaAssetList (options = {}) {
  const { auth, server, offset, limit, id, uuid, mediaId, mediaReferenceType, language } = options
  const params = omitBy(
    {
      offset,
      limit,
      id,
      uuid,
      mediaId: List.isList(mediaId) || Set.isSet(mediaId) ? mediaId.toJS() : mediaId,
      mediaReferenceType,
      language,
    },
    isUndefined,
  )
  const res = await request
    .get(`${server}/v1/media-assets/list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(qsStringifyQuery(params, { arrayFormat: 'indices', encodeValuesOnly: true }))
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a list of media assets ingest jobs
 * @param {Object} options The options
 * @param {import('immutable').Map} options.auth The auth
 * @param {String} options.server The server to request
 * @param {Number} options.id The media assets id
 */
export async function getMediaAssetIngestJobsList (options = {}) {
  const { auth, server, id } = options
  const res = await request
    .get(`${server}/v1/media-assets/${strictUriEncode(id)}/ingest/job-list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a list of media assets ingest url
 * @param {Object} options The options
 * @param {import('immutable').Map} options.auth The auth
 * @param {String} options.server The server to request
 * @param {Number} options.id The media assets id
 */
export async function getMediaAssetIngestUrlList (options = {}) {
  const { auth, server, id } = options
  const res = await request
    .get(`${server}/v1/media-assets/${strictUriEncode(id)}/ingest/url-list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(get(res, 'body', {}))
}

/**
 * Create media asset ingest request
 * @param {Object} options The options
 * @param {import('immutable').Map} options.auth The auth
 * @param {String} options.server The server to request
 * @param {Number} options.id The media assets id
 * @param {Map} options.master The the master file to ingest
 * @param {String} options.master.url The the master file to ingest
 */
export async function createMediaAssetIngestRequest (options = {}) {
  const { auth, server, id, master } = options
  if (!id) {
    throw new Error('The id option is required')
  }
  if (!Map.isMap(master)) {
    throw new Error('The master option is required to be a Map')
  }
  if (!master.get('url')) {
    throw new Error('The master.url propery is required')
  }
  const res = await request
    .post(`${server}/v1/media-assets/${strictUriEncode(id)}/ingest/request`)
    .send({
      master: { url: master.get('url') },
      priority: 'normal',
    })
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(get(res, 'body', {}))
}
